import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { HealthCheckCard } from '@/components/HealthCheckCard'
import { HealthCheckModal } from '@/components/HealthCheckModal'
import { useHealthChecks, type HealthCheck } from '@/lib/health-checks'
import { useServices, type Entity } from '@/lib/cluster'

const NO_STACK = '(no stack)'
const COLLAPSED_STORAGE_KEY = 'zeus.health.collapsedStacks'

function loadCollapsed(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(COLLAPSED_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {}
  } catch {
    return {}
  }
}

interface ServiceGroup {
  serviceId: string
  serviceName: string
  checks: HealthCheck[]
}

interface StackGroup {
  stack: string
  services: ServiceGroup[]
  totalChecks: number
  upCount: number
  downCount: number
  autoDisabledCount: number
}

function checkDot(c: HealthCheck): { color: string; kind: 'up' | 'down' | 'auto' | 'idle' | 'disabled' } {
  if (c.auto_disabled_at) return { color: 'bg-amber-500', kind: 'auto' }
  if (!c.enabled) return { color: 'bg-muted-foreground', kind: 'disabled' }
  if (c.last_status == null) return { color: 'bg-muted-foreground', kind: 'idle' }
  const ok = c.last_status >= c.expect_min && c.last_status <= c.expect_max
  return ok ? { color: 'bg-emerald-500', kind: 'up' } : { color: 'bg-destructive', kind: 'down' }
}

function stackOfService(svc: Entity | undefined, serviceName: string): string {
  // Prefer the Docker Swarm label; fall back to the convention <stack>_<svc>.
  const fromLabel = svc?.labels?.['com.docker.stack.namespace']
  if (fromLabel) return fromLabel
  const idx = serviceName.indexOf('_')
  if (idx > 0) return serviceName.slice(0, idx)
  return NO_STACK
}

export function HealthChecksPage() {
  const { data: checks, isLoading } = useHealthChecks()
  const { data: services } = useServices()
  const [editing, setEditing] = useState<HealthCheck | null>(null)
  const [creating, setCreating] = useState(false)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(loadCollapsed)

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(collapsed))
    } catch {
      // ignore quota errors
    }
  }, [collapsed])

  function toggle(stack: string) {
    setCollapsed((prev) => ({ ...prev, [stack]: !prev[stack] }))
  }
  function setAll(value: boolean) {
    if (!checks) return
    const next: Record<string, boolean> = {}
    const stacks = new Set<string>()
    const svcById = new Map<string, Entity>()
    for (const s of services ?? []) svcById.set(s.id, s)
    for (const c of checks) {
      stacks.add(stackOfService(svcById.get(c.service_id), c.service_name))
    }
    for (const s of stacks) next[s] = value
    setCollapsed(next)
  }

  const groups = useMemo<StackGroup[]>(() => {
    if (!checks) return []

    const svcById = new Map<string, Entity>()
    for (const s of services ?? []) svcById.set(s.id, s)

    // Group checks by (stack, service)
    const stacks = new Map<string, Map<string, ServiceGroup>>()
    for (const c of checks) {
      const stack = stackOfService(svcById.get(c.service_id), c.service_name)
      const svcMap = stacks.get(stack) ?? new Map<string, ServiceGroup>()
      const sg = svcMap.get(c.service_id) ?? {
        serviceId: c.service_id,
        serviceName: c.service_name,
        checks: [],
      }
      sg.checks.push(c)
      svcMap.set(c.service_id, sg)
      stacks.set(stack, svcMap)
    }

    // Build sorted, summarized groups
    const out: StackGroup[] = []
    for (const [stack, svcMap] of stacks) {
      const services = Array.from(svcMap.values()).sort((a, b) =>
        a.serviceName.localeCompare(b.serviceName),
      )
      let totalChecks = 0
      let upCount = 0
      let downCount = 0
      let autoDisabledCount = 0
      for (const sg of services) {
        for (const c of sg.checks) {
          totalChecks += 1
          const k = checkDot(c).kind
          if (k === 'up') upCount += 1
          else if (k === 'down') downCount += 1
          else if (k === 'auto') autoDisabledCount += 1
        }
        sg.checks.sort((a, b) => a.name.localeCompare(b.name))
      }
      out.push({ stack, services, totalChecks, upCount, downCount, autoDisabledCount })
    }
    out.sort((a, b) => {
      if (a.stack === NO_STACK) return 1
      if (b.stack === NO_STACK) return -1
      return a.stack.localeCompare(b.stack)
    })
    return out
  }, [checks, services])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Health checks</h1>
          <p className="text-muted-foreground">HTTP probes grouped by stack and service.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setAll(false)}>Expand all</Button>
          <Button variant="outline" size="sm" onClick={() => setAll(true)}>Collapse all</Button>
          <Button onClick={() => setCreating(true)}>+ New check</Button>
        </div>
      </div>

      {isLoading && <Skeleton className="h-40" />}
      {!isLoading && groups.length === 0 && (
        <p className="text-sm text-muted-foreground">No health checks configured yet.</p>
      )}

      <div className="space-y-6">
        {groups.map((g) => {
          const isCollapsed = collapsed[g.stack] === true
          return (
            <Card key={g.stack}>
              <CardHeader
                className="pb-2 cursor-pointer select-none"
                onClick={() => toggle(g.stack)}
              >
                <CardTitle className="flex items-center gap-3 text-lg">
                  {isCollapsed ? (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span>{g.stack}</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    {g.services.length} service{g.services.length !== 1 ? 's' : ''} ·{' '}
                    {g.totalChecks} check{g.totalChecks !== 1 ? 's' : ''}
                  </span>
                  <div className="flex gap-1">
                    {g.upCount > 0 && (
                      <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-500">
                        {g.upCount} up
                      </Badge>
                    )}
                    {g.downCount > 0 && (
                      <Badge variant="outline" className="text-[10px] border-destructive/40 text-destructive">
                        {g.downCount} down
                      </Badge>
                    )}
                    {g.autoDisabledCount > 0 && (
                      <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-500">
                        {g.autoDisabledCount} auto-disabled
                      </Badge>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              {!isCollapsed && (
                <CardContent className="space-y-4">
                  {g.services.map((sg) => (
                    <div key={sg.serviceId} className="space-y-2">
                      <div className="text-sm font-medium text-muted-foreground border-b pb-1">
                        {sg.serviceName}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {sg.checks.map((c) => (
                          <HealthCheckCard key={c.id} check={c} onEdit={setEditing} />
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          )
        })}
      </div>

      {(creating || editing) && (
        <HealthCheckModal
          open={true}
          initial={editing ?? undefined}
          onClose={() => { setCreating(false); setEditing(null) }}
        />
      )}
    </div>
  )
}
